import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../components/Button";
import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusTag } from "../../../../components/PlusTag";
import { Box as BarBox, StickySpacer } from "../../../../components/SaveBar/styles";
import { ScreenHeader } from "../../../../components/SubHeader";
import constants from "../../../../constants";
import { AppState, useAppState } from "../../../../contexts/App";
import { getPreviousPath } from "../../../../hooks/PreviousPath";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import ChevronLeftIcon from "../../../../icons/ChevronLeft";
import { StripeIcon } from "../../../../icons/Stripe";
import { getPages, siteHasAgendaPage } from "../../../../model/pages";
import { siteUsesPlusStyle } from "../../../../model/plusFeatures";
import { siteHasActivePlusSections } from "../../../../model/sectionDefinitions";
import { Site } from "../../../../model/site";
import { keepoInternalAnalyticsService } from "../../../../services/analytics";
import { api, ApiSiteResponse, hasActiveSubscription } from "../../../../services/api";
import CreationProgress from "../../../Editor/components/CreationProgress";
import { YEARLY_SAVING } from "../../pricing";
import { useFlowEditingSite } from "../../useFlowEditingSite";
import { CREATE_NEW_KEEPO } from "../SiteSelection";
import {
  Benefits,
  Container,
  Content,
  FlowBar,
  FlowHeader,
  FlowNotice,
  Hero,
  PlanCard,
  Plans,
  StickyBar,
  TopLinks,
} from "./styles";

// Keepo screens that link here, by path prefix, and the crumb that leads back to each (deeper
// screens, like a single section, collapse into their area). The dashboard is the root crumb.
const ORIGIN_CRUMBS: { prefix: string; labelKey?: string; label?: string; to: string }[] = [
  { prefix: "/keepo/plus", labelKey: "pages.plusBenefits.title", to: "/keepo/plus" },
  { prefix: "/settings", labelKey: "pages.settings.header.title", to: "/settings" },
  // The Analytics screen names itself the same in every language
  { prefix: "/analytics", label: "Analytics", to: "/analytics" },
  { prefix: "/editor", labelKey: "pages.keepoDashboard.actions.editor.title", to: "/editor/pages" },
];

// The PLUS benefits, two per row; `isNew` marks what's just been launched
const BENEFITS = [
  { key: "domain", icon: "🌐", isNew: true },
  { key: "agenda", icon: "📅", isNew: true },
  { key: "pages", icon: "📄", isNew: true },
  { key: "brand", icon: "✨" },
  { key: "style", icon: "🎨" },
  { key: "sections", icon: "🧩" },
  { key: "media", icon: "🎬" },
  { key: "analytics", icon: "📊" },
  { key: "ai", icon: "🤖", isNew: true },
  { key: "google", icon: "🔎", isNew: true },
  { key: "password", icon: "🔒" },
  { key: "chat", icon: "💬", isNew: true },
];

type Props = {
  site?: Site;
  setSite: (site: Site) => void;
};

export const FeaturesPage = ({ site, setSite }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryUrl = useQueryUrl();
  const { appState, setAppState } = useAppState();

  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<ApiSiteResponse[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSavingDraft, setSavingDraft] = useState(false);

  const [planType, setPlanType] = useState<"MONTHLY" | "ANNUALLY">("ANNUALLY");
  const editorFlow = queryUrl.get("flow");
  const isEditorFlow = editorFlow === "create" || editorFlow === "edit";
  const isPlusRequiredCreation = queryUrl.get("plusRequired") === "1";
  // Opened from a Keepo's CTA (not from the main nav or the publish flow): a breadcrumb leads back to it
  const querySiteId = queryUrl.get("site");
  const isFromKeepo = !isEditorFlow && !!querySiteId && querySiteId === appState.editingSiteId;
  const origin = ORIGIN_CRUMBS.find(({ prefix }) => {
    const previousPath = getPreviousPath() || "";
    return previousPath === prefix || previousPath.startsWith(`${prefix}/`);
  });
  // The screen it was opened from, within the app (not another step of the subscription)
  const previousPath = getPreviousPath()?.startsWith("/subscription") ? undefined : getPreviousPath();
  // Back to the screen it was opened from (e.g. a banner on /sites, a Keepo's screen). Past the free limit
  // it's also the way out besides subscribing: the draft is saved (the editor when there's no history)
  const backTo = isPlusRequiredCreation
    ? previousPath || "/editor/sections"
    : previousPath || (isFromKeepo ? origin?.to || "/keepo" : undefined);
  // The Keepo being created stays in the side preview, even after a reload
  useFlowEditingSite(isEditorFlow, querySiteId, site, setSite);
  const showNoPlusResourcesButton = !isPlusRequiredCreation && (isEditorFlow || !sites.length);
  const pageCount = getPages(site).length;

  // Payment happens on Keepo's own checkout screen (no longer on Stripe's page). The editor flow goes
  // along, so the checkout knows where it came from
  const callCheckoutHandler = useCallback(
    (siteId?: string) => {
      const params = new URLSearchParams({ plan: planType });
      if (siteId) params.set("site", siteId);
      if (editorFlow) params.set("flow", editorFlow);
      navigate(`/subscription/checkout?${params}`);
    },
    [editorFlow, navigate, planType]
  );

  const continueHandler = async () => {
    setLoading(true);
    const selectedSite = queryUrl.get("site");

    // Honor an explicit `?site=` before the "no other free site" shortcut below:
    // checkout without a siteId creates a brand-new PLUS Keepo, so a caller asking to
    // upgrade a specific (possibly unpublished) site must never fall through to it.
    if (!!selectedSite && selectedSite !== CREATE_NEW_KEEPO) {
      return callCheckoutHandler(selectedSite);
    }

    if (!sites.length) {
      return callCheckoutHandler();
    }

    navigate(`/subscription/selection?plan=${planType}`);
  };

  const noPlusResourcesHandler = useCallback(async () => {
    if (!isEditorFlow) {
      // Opened straight (e.g. from an email link): there's no screen to go back to in the app
      if (getPreviousPath()) {
        navigate(-1);
      } else {
        navigate("/sites");
      }
      return;
    }

    // After a reload the app starts over: the Keepo being edited is no longer in memory. It's
    // recovered from `?site=` (the URL every navigate into this flow builds from the editing Keepo)
    const querySiteId = queryUrl.get("site");
    let editing = { siteId: appState.editingSiteId, site, state: appState };
    if (!site || !appState.editingSiteId || (querySiteId && querySiteId !== appState.editingSiteId)) {
      setLoading(true);
      const sites = querySiteId ? await api.getSites().catch(() => []) : [];
      const found = sites.find(item => item.siteId === querySiteId);
      if (!found || !(found.draft || found.site)) {
        navigate("/sites");
        return;
      }
      const state = {
        ...appState,
        editingSiteId: found.siteId,
        published: !!found.site,
        editingSiteDisabled: found.disabled,
        editingSiteSubscription: found.subscription || { type: "FREE" },
      } as AppState;
      setAppState(state);
      setSite(found.draft || found.site);
      editing = { siteId: found.siteId, site: found.draft || found.site, state };
    }
    const editingSite = editing.site as Site;

    if (siteHasActivePlusSections(editingSite)) {
      navigate("/editor/sections?plusRequired=1");
      return;
    }
    // The Agenda, set up freely meanwhile: its page goes, or the Keepo goes PLUS (Páginas says why)
    if (siteHasAgendaPage(editingSite)) {
      navigate("/editor/pages?plusRequired=agenda");
      return;
    }
    // A Keepo being created could use the look's PLUS items freely: back to Personalizar, to its simple version
    if (!editing.state.published && siteUsesPlusStyle(editingSite)) {
      navigate("/editor/style?flow=publish&plusRequired=1");
      return;
    }

    // Publishing happens on the last step, which also handles the free Keepos limit
    navigate("/editor/publish/ready");
  }, [appState, isEditorFlow, navigate, queryUrl, setAppState, setSite, site]);

  const saveDraftAndLeave = async () => {
    setSavingDraft(true);
    try {
      if (site && appState.editingSiteId) {
        await api.saveDraft(appState.editingSiteId, site);
      }
      navigate("/sites");
    } catch {
      setErrorMessage(t("pages.editor.publish.errors.generic"));
      setSavingDraft(false);
    }
  };

  const loadSitesHandler = useCallback(async () => {
    const sites = (await api.getSites()).filter(site => !hasActiveSubscription(site.subscription) && !!site.site);
    setSites(sites);
    setLoading(false);
  }, []);

  const notifyPreCheckout = async () => {
    const user = await api.getUser();
    keepoInternalAnalyticsService.notifyPreCheckoutEvent(user);
  };

  useEffect(() => {
    loadSitesHandler();
  }, [loadSitesHandler]);

  useEffect(() => {
    notifyPreCheckout();
  }, []);

  // The sticky bar shows what is actually charged: the yearly total, or the monthly price
  const isYearly = planType === "ANNUALLY";
  const prefix = "pages.subscription.features.page";
  const stickyPrice = isYearly ? constants.price.year.value : constants.price.month.value;

  const plans = (
    <Plans role="radiogroup" aria-label={t(`${prefix}.plans.label`)}>
      <PlanCard
        type="button"
        role="radio"
        aria-checked={isYearly}
        $selected={isYearly}
        onClick={() => setPlanType("ANNUALLY")}>
        <span className="save">{t(`${prefix}.plans.save`, { value: YEARLY_SAVING })}</span>
        <span className="radio" aria-hidden="true" />
        <span className="name">
          <b>
            {t("pages.subscription.features.plans.annually.title")}
            <span className="discount">{constants.price.year.discount}</span>
          </b>
          <span>
            <s>{constants.price.year.oldValue}</s> {t(`${prefix}.plans.yearly`, { value: constants.price.year.value })}
          </span>
        </span>
        <span className="value">
          {constants.price.year.perMonth}
          <small>{t("pages.subscription.features.plans.perMonth")}</small>
        </span>
      </PlanCard>
      <PlanCard
        type="button"
        role="radio"
        aria-checked={!isYearly}
        $selected={!isYearly}
        onClick={() => setPlanType("MONTHLY")}>
        <span className="radio" aria-hidden="true" />
        <span className="name">
          <b>{t("pages.subscription.features.plans.monthly.title")}</b>
          <span>{t(`${prefix}.plans.monthly`)}</span>
        </span>
        <span className="value">
          {constants.price.month.value}
          <small>{t("pages.subscription.features.plans.perMonth")}</small>
        </span>
      </PlanCard>
    </Plans>
  );

  const benefits = (
    <ul>
      {BENEFITS.map(({ key, icon, isNew }) => (
        <li key={key}>
          <span className="icon" aria-hidden="true">
            {icon}
          </span>
          <span className="text">
            <b>
              {t(`${prefix}.benefits.${key}.title`)}
              {isNew && <span className="new">{t(`${prefix}.benefits.new`)}</span>}
            </b>
            <span>{t(`${prefix}.benefits.${key}.text`)}</span>
          </span>
        </li>
      ))}
    </ul>
  );

  if (loading) {
    return <LoadingModal />;
  }

  return (
    <Container $flow={isEditorFlow}>
      {/* A step of creating/editing a Keepo: the flow's header and steps on top (its preview is on the side) */}
      {isEditorFlow ? (
        <FlowHeader>
          {/* where the person is in the flow, on top; the screen's own header under it */}
          <CreationProgress step="ready" />
          <ScreenHeader
            title={t("pages.subscription.flow.plusTitle")}
            back={{
              label: t(`pages.editor.publish.ready.back.${editorFlow}`),
              to: editorFlow === "create" ? "/editor/publish" : "/editor/style?flow=publish",
            }}
            // Past the free limit, not subscribing now: the Keepo stays as a draft, back to the Keepos list
            rightElement={
              isPlusRequiredCreation && (
                <button type="button" className="save-draft" disabled={isSavingDraft} onClick={saveDraftAndLeave}>
                  {isSavingDraft ? t(`${prefix}.saveDraft.saving`) : t(`${prefix}.saveDraft.title`)}
                </button>
              )
            }
          />
        </FlowHeader>
      ) : (
        <TopLinks>
          {backTo && (
            <button type="button" onClick={() => (getPreviousPath() === backTo ? navigate(-1) : navigate(backTo))}>
              <ChevronLeftIcon width={10} height={10} color="currentColor" />
              {t("pages.subscription.features.plusRequired.back")}
            </button>
          )}
          {/* Past the free limit, not subscribing now: the Keepo stays as a draft, back to the Keepos list */}
          {isPlusRequiredCreation && (
            <button type="button" className="save-draft" disabled={isSavingDraft} onClick={saveDraftAndLeave}>
              {isSavingDraft ? t(`${prefix}.saveDraft.saving`) : t(`${prefix}.saveDraft.title`)}
            </button>
          )}
        </TopLinks>
      )}
      <Content $flow={isEditorFlow}>
        <Hero>
          <PlusTag>✦ Keepo Plus</PlusTag>
          {/* Created past the free limit: publishing it needs PLUS, so the pitch says why we're asking to subscribe */}
          <h1>
            {isPlusRequiredCreation
              ? t("pages.subscription.features.plusRequired.title")
              : t("pages.subscription.header.description")}
          </h1>
          <p className="subtitle">
            {isPlusRequiredCreation
              ? t("pages.subscription.features.plusRequired.description")
              : t(`${prefix}.subtitle`)}
          </p>

          {plans}

          {errorMessage && <p className="error">{errorMessage}</p>}
          <div className="secure">
            <span>🔒 {t("pages.subscription.paymentInfo")}</span>
            <StripeIcon />
          </div>
          <p className="info">{t("pages.subscription.features.pricesInfo")}</p>
        </Hero>

        <Benefits>
          <h2>{t("pages.subscription.features.groups.title")}</h2>
          {benefits}
        </Benefits>

        {showNoPlusResourcesButton && !isEditorFlow && (
          <button
            type="button"
            onClick={noPlusResourcesHandler}
            className="mt-10 w-full py-2 text-center font-inter text-sm text-neutral-500 transition-colors hover:text-neutral-300">
            {t("pages.subscription.features.noPlusResources")}
          </button>
        )}
      </Content>

      {/* The subscribe CTA stays pinned to the bottom on every screen size */}
      {isEditorFlow ? (
        <>
          {/* The flow's fixed bar, as in its other steps: the way on without PLUS, and subscribing */}
          <StickySpacer />
          <BarBox $sticky>
            {/* Going on without PLUS publishes only the home: its pages stay saved for when it subscribes */}
            {!isPlusRequiredCreation && pageCount > 0 && (
              <FlowNotice>
                {t(`pages.subscription.flow.${pageCount === 1 ? "pageNotice" : "pagesNotice"}`, { count: pageCount })}
              </FlowNotice>
            )}
            <FlowBar>
              {isPlusRequiredCreation ? (
                <LinkButton onClick={() => navigate(backTo || "/editor/publish")}>
                  {t("pages.editor.templates.back")}
                </LinkButton>
              ) : (
                <LinkButton onClick={noPlusResourcesHandler}>{t("pages.subscription.flow.withoutPlus")}</LinkButton>
              )}
              <Button type="INFO" size="LG" onClick={continueHandler}>
                {t("pages.subscription.flow.subscribe", { value: stickyPrice })}
              </Button>
            </FlowBar>
          </BarBox>
        </>
      ) : (
        <StickyBar>
          <span className="summary">
            <span className="plan">
              {isYearly ? t(`${prefix}.bar.yearly`, { value: YEARLY_SAVING }) : t(`${prefix}.bar.monthly`)}
            </span>
            <span className="price">
              {stickyPrice}
              <small>
                {isYearly
                  ? t("pages.subscription.features.plans.perYear")
                  : t("pages.subscription.features.plans.perMonth")}
              </small>
            </span>
          </span>
          <button type="button" onClick={continueHandler} className="plus-cta">
            {t("pages.subscription.continueButton")}
            <span className="arrow" aria-hidden="true">
              →
            </span>
          </button>
        </StickyBar>
      )}
    </Container>
  );
};
